import axios from '../axios';

/**
 * Fetch paginated audit logs (Superadmin only).
 * @param {Object} params - dateFrom, dateTo, actor, action, resourceType, outcome, search, page, limit
 * @returns {Promise<{ data: Array, total: number, page: number, limit: number }>}
 */
export async function getAuditLogs(params = {}) {
  const response = await axios.get('/api/audit/logs', { params });
  return response.data;
}

/**
 * Fetch filter options for Actor and Action/event type dropdowns (Superadmin only).
 * @param {Object} params - optional dateFrom, dateTo (defaults to last 90 days)
 * @returns {Promise<{ actors: Array<{ value, label }>, actions: Array<{ value }> }>}
 */
export async function getAuditFilterOptions(params = {}) {
  const response = await axios.get('/api/audit/filter-options', { params });
  return response.data;
}

/**
 * Export audit logs as CSV (Superadmin only). Triggers download; export is audited on backend.
 * @param {Object} params - same as getAuditLogs (dateFrom, dateTo, actor, action, resourceType, outcome, search)
 */
export async function exportAuditLogs(params = {}) {
  const { data, headers } = await axios.get('/api/audit/logs/export', {
    params,
    responseType: 'blob',
  });
  const disposition = headers['content-disposition'];
  const filename =
    disposition && /filename="?([^";\n]+)"?/.test(disposition)
      ? /filename="?([^";\n]+)"?/.exec(disposition)[1]
      : `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Fire-and-forget helper to record client-side PHI actions
 * (export/print/share) in the backend HIPAA audit log.
 *
 * The backend endpoint requires authentication (x-auth header).
 *
 * @param {Object} params
 * @param {"export"|"print"|"share"} params.action
 * @param {string} params.resourceType - e.g. "report", "patient_list"
 * @param {string} [params.resourceId]
 * @param {string} [params.format] - e.g. "pdf", "xlsx"
 * @param {Object} [params.metadata] - non-PHI extra context
 */
export async function trackPhiAction({
  action,
  resourceType,
  resourceId,
  format,
  metadata = {},
}) {
  if (!action || !resourceType) return;

  try {
    await axios.post('/api/audit/phi-action', {
      action,
      resourceType,
      resourceId,
      format,
      metadata,
    });
  } catch (e) {
    // Intentionally swallow errors – audit failures must not break UX
    // eslint-disable-next-line no-console
    console.error('[audit.service] Failed to track PHI action', e?.message || e);
  }
}

